// @flow

import React from "react";
import { type GraphQLSchema, type GraphQLType, type GraphQLField, type GraphQLEnumValue, GraphQLObjectType, GraphQLNonNull, GraphQLList, GraphQLEnumType, GraphQLUnionType, GraphQLInterfaceType } from 'graphql';

import Scalar from "./GraphQLExplorerDocumentation/Scalar";
import { getGraphQLSchema } from "./schema";

type Props = {
  params: {
    name: string
  }
};

export default class GraphQLExplorerDocumentationType extends React.PureComponent<Props> {
  render() {
    const typename = this.props.params.name;
    const schema: GraphQLSchema = getGraphQLSchema();
    const type = schema.getType(typename);

    if (!type) {
      return (
        <div>
          <h2>Not Found</h2>
          <p>No type with that name could be found.</p>
        </div>
      );
    }

    let objectType: string = 'type';

    if (type instanceof GraphQLEnumType) {
      objectType = 'enum';
    } else if (type instanceof GraphQLUnionType) {
      objectType = 'union type';
    } else if (type instanceof GraphQLInterfaceType) {
      objectType = 'interface';
    }

    // TODO: cm-s-graphql is a hack; make it suck less
    return (
      <div className="cm-s-graphql">
        <h2>{objectType[0].toUpperCase() + objectType.slice(1)}: <Scalar name={typename} className="monospace" /></h2>
        <p>{type.description || 'No description available.'}</p>
        {this.renderFields(type)}
        {this.renderValues(type)}
        {this.renderTypes(type)}
        {this.renderInterfaces(type)}
        {this.renderPossibleTypes(type, schema)}
      </div>
    );
  }

  renderFields(type: GraphQLType) {
    if (!(type instanceof GraphQLInterfaceType) && !(type instanceof GraphQLObjectType)) {
      return null;
    }

    // NOTE: This is a weird type cast, but Object.values makes it tricky.
    const fields: Array<GraphQLField<any, any>> = (Object.values(type.getFields()): any);

    return (
      <React.Fragment>
        <h3 name="fields">Fields</h3>
        {fields.map(this.renderField)}
      </React.Fragment>
    );
  }

  renderField = (field: GraphQLField<any, any>) => {
    let nonNull = false;
    let array = false;
    let nonNullItems = false;
    let type = field.type;

    if (type instanceof GraphQLNonNull) {
      nonNull = true;
      type = type.ofType;
    }

    if (type instanceof GraphQLList) {
      array = true;
      type = type.ofType;
    }

    if (type instanceof GraphQLNonNull) {
      nonNullItems = true;
      type = type.ofType;
    }

    if (type instanceof GraphQLList) {
      throw new TypeError("Type was still a List after processing. This shouldn't be possible.");
    }

    return (
      <React.Fragment key={field.name}>
        <h4 id={`fields-${field.name}`} className="monospace">
          <span className="cm-property">{field.name}</span>
          {this.renderFieldArguments(field)}
          <span className="cm-punctuation">: {array && '['}</span>
          <Scalar name={type.name} />
          <span className="cm-punctuation">
            {nonNullItems && '!'}
            {array && ']'}
            {nonNull && '!'}
          </span>
        </h4>
        {field.isDeprecated && <p>This field is deprecated. {field.deprecationReason}</p>}
        <p>{field.description || 'No description available.'}</p>
      </React.Fragment>
    );
  }

  renderFieldArguments(field: GraphQLField<any, any>) {
    if (!field.args || field.args.length < 1) {
      return null;
    }

    const renderedArguments = field.args.map((argument, index, list) => {
      let nonNull = false;
      let array = false;
      let nonNullItems = false;
      let type = argument.type;

      if (type instanceof GraphQLNonNull) {
        nonNull = true;
        type = type.ofType;
      }

      if (type instanceof GraphQLList) {
        array = true;
        type = type.ofType;
      }

      if (type instanceof GraphQLNonNull) {
        nonNullItems = true;
        type = type.ofType;
      }

      if (type instanceof GraphQLList) {
        throw new TypeError("Type was still a List after processing. This shouldn't be possible.");
      }

      return (
        <React.Fragment key={argument.name}>
          <span className="cm-attribute">{argument.name}</span>
          <span className="cm-punctuation">: {array && '['}</span>
          <Scalar name={type.name} />
          <span className="cm-punctuation">
            {nonNullItems && '!'}
            {array && ']'}
            {nonNull && '!'}
            {index < list.length - 1 && ', '}
          </span>
        </React.Fragment>
      );
    });

    return (
      <React.Fragment>
        <span className="cm-punctuation">(</span>
        {renderedArguments}
        <span className="cm-punctuation">)</span>
      </React.Fragment>
    );
  }

  renderValues = (type: GraphQLType) => {
    if (!(type instanceof GraphQLEnumType)) {
      return null;
    }

    return (
      <React.Fragment>
        <h3>Possible Values</h3>
        {type.getValues().map(this.renderValue)}
      </React.Fragment>
    );
  }

  renderValue(value: GraphQLEnumValue) {
    return (
      <React.Fragment key={value.name}>
        <h4 id={`values-${value.name}`}><span className="cm-string-2 monospace">{value.name}</span></h4>
        {value.isDeprecated && <p>This value is deprecated. {value.deprecationReason}</p>}
        <p>{value.description || 'No description available.'}</p>
      </React.Fragment>
    );
  }

  renderTypes(type: GraphQLType) {
    if (!(type instanceof GraphQLUnionType)) {
      return null;
    }

    return (
      <React.Fragment>
        <h3 id="types">Possible Types</h3>
        {type.getTypes().map(this.renderType)}
      </React.Fragment>
    );
  }

  renderType(type: GraphQLObjectType) {
    return (
      <React.Fragment key={type.name}>
        <h4 id={`types-${type.name}`}><Scalar name={type.name} className="monospace" /></h4>
        <p>{type.description || 'No description available.'}</p>
      </React.Fragment>
    );
  }

  renderInterfaces(type: GraphQLType) {
    if (!(type instanceof GraphQLObjectType)) {
      return null;
    }

    const interfaces = type.getInterfaces();

    if (interfaces.length < 1) {
      return null;
    }

    return (
      <React.Fragment>
        <h3 id="interfaces">Interfaces</h3>
        {interfaces.map(this.renderInterface)}
      </React.Fragment>
    );
  }

  renderInterface(iface: GraphQLInterfaceType) {
    return (
      <React.Fragment key={iface.name}>
        <h5><Scalar name={iface.name} className="monospace" /></h5>
        <p>{iface.description || 'No description available.'}</p>
      </React.Fragment>
    );
  }

  renderPossibleTypes(type: GraphQLType, schema: GraphQLSchema) {
    if (!(type instanceof GraphQLUnionType)) {
      return;
    }

    const implementors = schema.getPossibleTypes(type);

    if (!implementors || implementors.length < 1) {
      return null;
    }

    return (
      <React.Fragment>
        <h3 id="implemented-by">Implemented By</h3>
        <span className="monospace">
          {implementors.map((implementor, index, implementors) => (
            <React.Fragment key={implementor.name}>
              <Scalar name={implementor.name} />
              {(index < implementors.length - 2) && (
                <span className="cm-punctuation">
                  {', '}
                </span>
              )}
              {index === implementors.length - 2 && ' and '}
            </React.Fragment>
          ))}
        </span>
      </React.Fragment>
    );
  }
}
